import { Injectable } from '@angular/core';
import { Events, LoadingController, Platform } from 'ionic-angular';
import { Globals } from '../../globals';
import { TranslateService } from '@ngx-translate/core';
import { AngularFirestore } from 'angularfire2/firestore';
import * as moment from 'moment';

@Injectable()
export class AnalyticsServices {
  // Cola para reintentos de analytics fallidos (se guarda en memoria)
  private analyticsQueue: any[] = [];
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY_MS = 2000;

  constructor(public events: Events,
    public loadingCtrl: LoadingController,
    public platform: Platform,
    public translate: TranslateService,
    public globals: Globals,
    public afs: AngularFirestore) {
  }

  /**
   * Método auxiliar para enviar datos a _data con manejo de errores y reintentos
   */
  private async sendToAnalytics(
    dataType: string, 
    data: any, 
    requiredFields: string[] = []
  ): Promise<boolean> {
    // Validar campos obligatorios
    for (const field of requiredFields) {
      if (!data[field]) {
        console.error(`[Analytics] Campo obligatorio faltante en ${dataType}:`, field, data);
        return false;
      }
    }

    // Intentar enviar con reintentos
    for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
      try {
        await this.afs.collection('_data').add(data);
        
        if (attempt > 1) {
          console.log(`[Analytics] ${dataType} enviado correctamente en intento ${attempt}`);
        }
        return true;
      } catch (error) {
        console.error(`[Analytics] Error en intento ${attempt}/${this.MAX_RETRIES} para ${dataType}:`, error);
        
        if (attempt === this.MAX_RETRIES) {
          // Último intento fallido, guardar en cola para reintento posterior
          this.queueForRetry(dataType, data);
          return false;
        }
        
        // Esperar antes del siguiente intento (backoff exponencial)
        await this.delay(this.RETRY_DELAY_MS * attempt);
      }
    }
    
    return false;
  }

  /**
   * Guardar en cola local para reintento posterior
   */
  private queueForRetry(dataType: string, data: any): void {
    this.analyticsQueue.push({
      type: dataType,
      data: data,
      timestamp: new Date().toISOString()
    });
    
    console.warn(`[Analytics] ${dataType} guardado en cola para reintento. Cola actual: ${this.analyticsQueue.length} elementos`);
    
    // Limitar tamaño de cola a 100 elementos para no llenar memoria
    if (this.analyticsQueue.length > 100) {
      const removed = this.analyticsQueue.shift();
      console.warn(`[Analytics] Cola llena. Eliminando elemento más antiguo:`, removed.type);
    }
  }

  /**
   * Método auxiliar para delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Método público para reintentar envío de elementos en cola
   */
  public async retryQueuedAnalytics(): Promise<number> {
    if (this.analyticsQueue.length === 0) {
      return 0;
    }

    console.log(`[Analytics] Reintentando ${this.analyticsQueue.length} analytics en cola...`);
    let successCount = 0;
    const itemsToRetry = [...this.analyticsQueue];
    this.analyticsQueue = [];

    for (const item of itemsToRetry) {
      try {
        await this.afs.collection('_data').add(item.data);
        successCount++;
      } catch (error) {
        console.error(`[Analytics] Error al reintentar ${item.type}:`, error);
        // Volver a agregar a la cola si falla
        this.analyticsQueue.push(item);
      }
    }

    console.log(`[Analytics] Reintentos completados: ${successCount}/${itemsToRetry.length} exitosos`);
    return successCount;
  }

  /**
   * Obtener el estado actual de la cola de analytics
   */
  public getQueueStatus(): { count: number; items: any[] } {
    return {
      count: this.analyticsQueue.length,
      items: this.analyticsQueue.map(item => ({
        type: item.type,
        timestamp: item.timestamp
      }))
    };
  }

  /**
   * Limpiar la cola de analytics (usar con precaución)
   */
  public clearQueue(): number {
    const count = this.analyticsQueue.length;
    this.analyticsQueue = [];
    console.warn(`[Analytics] Cola limpiada manualmente. Se eliminaron ${count} elementos`);
    return count;
  }

  sendSalesSales(sales_sales) {
    this.afs.collection('_data').add({
      'uid': sales_sales.uid,
      'uid_doc': sales_sales.uid_doc,
      'date': moment(sales_sales.date).format('YYYY-MM-DD HH:mm:ss'),
      'sales': sales_sales.sales,
      'type': sales_sales.type,
      'type_type': sales_sales.type_type,
      'persons': sales_sales.persons,
      'zone': sales_sales.zone,
      'discount_type': sales_sales.discount_type,
      'discount': sales_sales.discount,
      'foodcost': sales_sales.foodcost,
      'tip': sales_sales.tip,
      'app_section': sales_sales.app_section,
      'text': sales_sales.text,
      'waiter': sales_sales.waiter
    });
  }

  sendSalesElements(sales_elements) {
    this.afs.collection('_data').add({
      'uid': sales_elements.uid,
      'uid_doc': sales_elements.uid_doc,
      'date': moment(sales_elements.date).format('YYYY-MM-DD HH:mm:ss'),
      'type': sales_elements.type,
      'list': sales_elements.list,
    });
  }

  sendWaiter(waiter) {
    this.afs.collection('_data').add({
      'uid': waiter.uid, //uid restaurante
      'waiter': waiter.waiter, //nombre del waiter
      'elements': waiter.elements, //list de elements
      'date': moment(waiter.date).format('YYYY-MM-DD HH:mm:ss'), //date 
      'type': waiter.type, //tipo de informacion en _data (WAITER)
      'type_type': waiter.type_type,//que tipo de modificacion es (ADD | MODIFY | DELETE)
    });
  }

  sendSalesDelivery(sales_delivery) {
    this.afs.collection('_data').add({
      "uid": sales_delivery.uid,//uid_restaurant
      "uid_doc": sales_delivery.uid_doc,//uid del ticket
      "date": moment(sales_delivery.date).format('YYYY-MM-DD HH:mm:ss'),//date
      "sales": sales_delivery.sales,//amount
      "type": sales_delivery.type,//el type para el switch de cloud funtions 
      "address": sales_delivery.address,//la address del customer
      "delivery": sales_delivery.delivery//delivery utilizado
    });
  }

  sendDeleteSales(delete_sales) {
    this.afs.collection('_data').add({
      "uid": delete_sales.uid,//uid_restaurant
      "uid_doc": delete_sales.uid_doc,//uid del ticket
      "date": moment(delete_sales.date).format('YYYY-MM-DD HH:mm:ss'),//date
      "type": delete_sales.type,//el type para el switch de cloud funtions 
    });
  }

  /**
   * Enviar datos de stock (CRÍTICO - con manejo de errores mejorado)
   */
  async sendStock(stock): Promise<boolean> {
    const data = {
      "uid": stock.uid,//uid_restaurant
      "date": moment(stock.date).format('YYYY-MM-DD HH:mm:ss'),//date
      "type": stock.type || 'stock',//el type para el switch de cloud funtions
      "comment": stock.comment || '', //comentario de la actualizacion de stock
      "type_type": stock.type_type, //de donde sale la modificacion ticket, stock, manual etc
      "elements": stock.elements || [], //Array con todos los elementos que se han modificado el stock
      "delivery_uid": stock.delivery_uid //UID del albaran si lo tenemos
    };

    // Campos obligatorios para stock
    const requiredFields = ['uid', 'type_type'];
    if (stock.delivery_uid) {
      requiredFields.push('delivery_uid'); // Si viene delivery_uid, es obligatorio
    }

    return await this.sendToAnalytics('sendStock', data, requiredFields);
  }

  /**
   * Enviar datos de compras de stock (CRÍTICO - con manejo de errores mejorado)
   */
  async sendStockPurchases(stock_purchases): Promise<boolean> {
    const data = {
      "uid": stock_purchases.uid,//uid_restaurant
      "date": moment(stock_purchases.date).format('YYYY-MM-DD HH:mm:ss'),//date
      "type": stock_purchases.type || 'stock_purchases',//el type para el switch de cloud funtions
      "delivery_uid": stock_purchases.delivery_uid //uid del albaran
    };

    // Campos obligatorios
    const requiredFields = ['uid', 'delivery_uid'];

    return await this.sendToAnalytics('sendStockPurchases', data, requiredFields);
  }

  kitchenElement(kitchen_elements) {
    this.afs.collection('_data').add({
      "uid": kitchen_elements.uid,//uid_restaurant
      "uid_doc": kitchen_elements.uid_doc,//uid del documento en firebase
      "type": "kitchen_elements",//el type para el switch de cloud funtions
      "user_name": kitchen_elements.user_name, //nombre del usuario que a acabado la comanda
      "list": kitchen_elements.list, // listado de elementos de la order que an sido finalizados 
      "date": moment(kitchen_elements.date).format('YYYY-MM-DD HH:mm:ss')
    });
  }

  kitchenOrders(kitchen_orders) {
    this.afs.collection('_data').add({
      "uid": kitchen_orders.uid,//uid_restaurant
      "uid_doc": kitchen_orders.uid_doc,//uid del documento en firebase
      "total_elements": kitchen_orders.total_elements,//total de elementos de la order
      "user_name": kitchen_orders.user_name, //nombre del usuario que a acabado la comanda
      "type": "kitchen_orders",//el type para el switch de cloud funtions
      "type_type": kitchen_orders.type_type,//el type para el switch de cloud funtions
      "date": moment(kitchen_orders.date).format('YYYY-MM-DD HH:mm:ss')
    });
  }

  cancelTableTicket(table_ticket) {
    this.afs.collection('_data').add({
      'uid': table_ticket.uid,//uid_restaurant
      'type': 'cancel_table_ticket',
      'number': table_ticket.number,//numero ticket cancelado
      'cash': table_ticket.cash,//caja que dio el numero ticket
      'cash_uid': table_ticket.cash_uid,//uid de la caja
      'type_type': table_ticket.type,//table o tk (sitio donde se ejecuto)
      'amount': table_ticket.amount,//total mesa
      'name': table_ticket.name,// nombre mesa/tk
      'waiter': table_ticket.waiter,//camrero que ejecuto accion
      'app_section': table_ticket.app_section,//app donde se ejecuto
      'date': moment(table_ticket.date).format('YYYY-MM-DD HH:mm:ss')
    });
  }

  ticketTimeline(ticket_timeline) {
    if (this.globals.searchNonUndefinedInObject(ticket_timeline)) {
      this.afs.collection('_data').add({
        'uid': ticket_timeline.uid,//uid_restaurant
        'uid_doc': ticket_timeline.uid_doc,//uid_doc (4 caracteres de la uid restaurante + 4 caracteres de la uid table + Date()+.now )
        'uid_table': ticket_timeline.uid_table,//uid_table
        'uid_ticket': ticket_timeline.uid_ticket,//uid_ticket
        'type': 'ticket_timeline',
        'type_type': ticket_timeline.type, //la accion
        'waiter': ticket_timeline.waiter, //quien lo ha hecho
        'uid_waiter': ticket_timeline.uid_waiter, //la uid del usuario
        'movement': ticket_timeline.movement, //lo que se ha hecho
        'app_section': ticket_timeline.app_section,//app donde se ejecuto
        'date': moment(ticket_timeline.date).format('YYYY-MM-DD HH:mm:ss')
      });
    }
  }

  trackingExp(event, exp, element_uid) {
    this.afs.collection('_data').add({
      'uid': this.globals.restaurant_uid,
      'type': 'plg_cartamenu',
      'app_section': 'APP2',
      'date': moment().format('YYYY-MM-DD HH:mm:ss'),
      'event': event,
      'exp': exp,
      'email': this.globals.restaurant.email,
      'element_uid': element_uid,
      'eb': this.globals.restaurant.plg_cartamenu_winner || false
    });
  }

  trackingExpReservations(event, exp) {
    this.afs.collection('_data').add({
      'uid': this.globals.restaurant_uid,
      'type': 'plg_reservas',
      'app_section': 'APP2',
      'date': moment().format('YYYY-MM-DD HH:mm:ss'),
      'event': event,
      'exp': exp,
      'email': this.globals.restaurant.email,
      'eb': this.globals.restaurant.plg_reservas_winner || false
    });
  }

  trackingExpArticles(event, exp, element_uid) {
    this.afs.collection('_data').add({
      'uid': this.globals.restaurant_uid,
      'type': 'plg_articles',
      'app_section': 'APP2',
      'date': moment().format('YYYY-MM-DD HH:mm:ss'),
      'event': event,
      'exp': exp,
      'email': this.globals.restaurant.email,
      'element_uid': element_uid,
      'eb': this.globals.restaurant.plg_articles_winner || false
    });
  }

  trackingExpPedidos(event, exp) {
    this.afs.collection('_data').add({
      'uid': this.globals.restaurant_uid,
      'type': 'plg_pedidos',
      'app_section': 'APP2',
      'date': moment().format('YYYY-MM-DD HH:mm:ss'),
      'event': event,
      'exp': exp,
      'email': this.globals.restaurant.email,
      'eb': this.globals.restaurant.plg_pedidos_winner || false
    });
  }

  /**
   * Tracking de eventos de stock (con manejo de errores mejorado)
   */
  async trackingExpStock(event, exp, element_uid): Promise<boolean> {
    const data = {
      'uid': this.globals.restaurant_uid,
      'type': 'plg_stock',
      'app_section': 'APP2',
      'date': moment().format('YYYY-MM-DD HH:mm:ss'),
      'event': event,
      'exp': exp,
      'email': this.globals.restaurant.email,
      'element_uid': element_uid,
      'eb': this.globals.restaurant.plg_stock_winner || false
    };

    // Campos obligatorios
    const requiredFields = ['uid', 'event'];

    return await this.sendToAnalytics('trackingExpStock', data, requiredFields);
  }

  trackingExpPos(event, exp, type) {
    this.afs.collection('_data').add({
      'uid': this.globals.restaurant_uid,
      'type': 'plg_pos',
      'app_section': 'APP2',
      'date': moment().format('YYYY-MM-DD HH:mm:ss'),
      'event': event,
      'exp': exp,
      'email': this.globals.restaurant.email,
      'eb': this.globals.restaurant.plg_pos_winner || false,
      'type_type': type
    });
  }
}


// WEBPACK FOOTER //
// ./src/pages/services/analyticsServices.ts